<script>
  import { afterUpdate } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';
  import { format as formatDate } from 'date-fns';

  import { SparkChart } from '@client/components/charts';
  import { Icon } from '@client/components/icon';
  import { Spinner } from '@client/components/spinner';

  import { CampaignsService } from '@client/services/campaigns';

  import { consolidateMetrics, formatRate } from '@client/utils/banners';
  import { determineLookBackPeriod } from '@client/utils/campaigns';

  import { getDateFromToday } from '@common/utils/dates';
  import { debounce } from '@common/utils/debounce';

  export let row = {};

  let loading = false;

  let windowWidth = window.innerWidth;

  let metricsCache = {};
  let metrics = {
    delivered: {
      color: '#43A0FC',
      label: $format('label.DELIVERIES'),
      data: []
    },
    views: {
      color: '#D9BC61',
      label: $format('label.IMPRESSIONS'),
      data: []
    },
    clicks: {
      color: '#92E2C0',
      label: $format('label.CLICKS'),
      data: []
    }
  };

  let data = {};

  const styles = 'border-width: 2px; color: #0087ff; height: 16px; width: 16px;';

  const generateMetricDataset = (series, label, color) => {
    const labels = [];
    const points = [];

    series.sort(([a], [b]) => new Date(a) - new Date(b));

    series.forEach(([x, y]) => {
      labels.push(formatDate(x, 'EEE, MMM d, yyyy'));
      points.push(y);
    });

    if (series?.length) {
      return {
        labels,
        datasets: [
          {
            label,
            backgroundColor: 'rgba(225, 204,230, .1)',
            borderColor: color,
            pointColor: color,
            fill: true,
            tension: 0.3,
            data: points
          }
        ]
      };
    }

    return null;
  };

  let startDate = formatDate(getDateFromToday(365), 'MM/dd/yyyy');
  let endDate = formatDate(getDateFromToday(0), 'MM/dd/yyyy');

  const update = async () => {
    loading = true;

    const id = row._id;

    if (!metricsCache[id]) {
      const days = determineLookBackPeriod(row, startDate);
      const response = await CampaignsService.getMetrics({ aid: row._id, days });

      // const filteredData = response.filter((item) => {
      //   const timesliceDate = subDays(new Date(item.timeslice), -1);
      //   return timesliceDate >= new Date(startDate) && timesliceDate <= new Date(endDate);
      // });

      data = consolidateMetrics(response, row);

      const series = {
        delivered: {
          color: '#43A0FC',
          label: $format('label.DELIVERIES'),
          data: []
        },
        views: {
          color: '#D9BC61',
          label: $format('label.IMPRESSIONS'),
          data: []
        },
        clicks: {
          color: '#92E2C0',
          label: $format('label.CLICKS'),
          data: []
        }
      };

      series.delivered.data = generateMetricDataset(
        [...(data.deliveries || 0)],
        series.delivered.label,
        series.delivered.color
      );
      series.views.data = generateMetricDataset(data.combinedViews, series.views.label, series.views.color);
      series.clicks.data = generateMetricDataset(data.combinedClicks, series.clicks.label, series.clicks.color);

      series.delivered.total = data.totals.ad;
      series.views.total = data.totals.combinedViews;
      series.clicks.total = data.totals.combinedClicks;

      metricsCache[id] = { ...series };
      metrics = { ...series };
    } else {
      metrics = { ...metricsCache[id] };
    }

    loading = false;
  };

  const onResize = debounce(() => {
    windowWidth = window.innerWidth;
  }, 150);

  afterUpdate(update);
</script>

<div class="charts">
  {#each Object.entries(metrics) as [key, config]}
    <div class="chart">
      <h3>
        {config.label}
        {#if !loading}
          ({config.total?.toLocaleString()})
        {/if}
        {#if key === 'clicks'}
          {#if !row.disableviews}
            CTR {formatRate(data?.totals?.combinedCtr)}
          {:else}
            CTRR {formatRate(data?.totals?.combinedCtrr)}
            <Icon id="no-ctr-{row._id}" name="alert-triangle" />

            <Tooltip target="no-ctr-{row._id}" placement="right">
              {$format('label.TOOLTIP.NO_CTR')}
            </Tooltip>
          {/if}
        {/if}
        {#if key === 'views'}
          {#if row.disableviews}
            <Icon id="disabled-views-{row._id}" name="alert-triangle" />

            <Tooltip target="disabled-views-{row._id}" placement="right">
              {$format('label.TOOLTIP.DISABLED_VIEWS')}
            </Tooltip>
          {/if}
        {/if}
      </h3>
      {#if !loading}
        {#if config.data}
          <SparkChart bind:data={config.data} height="35px" />
        {:else}
          <small>{$format('label.NO_DATA_AVAILABLE')}</small>
        {/if}
      {:else}
        <Spinner show inline {styles} />
      {/if}
    </div>
  {/each}
</div>

<svelte:window on:resize={onResize} />

<style lang="scss">
  .charts {
    align-items: center;
    display: grid;
    grid-template-columns: repeat(3, minmax(120px, 200px));
    gap: 24px;

    .chart {
      height: 40px;
      max-width: 160px;
      position: relative;
      z-index: 7;

      :global(.spinner) {
        justify-content: flex-start;
      }

      &:hover {
        z-index: 8;
      }

      small {
        color: var(--color-gray-700);
        font-size: var(--font-size-small);
      }

      h3 {
        font-size: var(--font-size-medium);
      }
    }
  }

  @media only screen and (max-width: 1485px) {
    .charts {
      gap: 36px;

      &:last-child {
        margin-bottom: 18px;
      }
    }
  }

  @media only screen and (max-width: 1098px) {
    .charts {
      grid-template-columns: repeat(1, minmax(120px, 1fr));

      .chart {
        max-width: 100%;
      }
    }
  }
</style>
